import React, { useRef, useMemo, useImperativeHandle, useContext, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import {
    Dialog, DialogContent, Box, Grid, Paper, Typography, Chip, IconButton, Tooltip, Button,
    Divider, Popover
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PrintIcon from '@material-ui/icons/Print';
import ListAltIcon from '@material-ui/icons/ListAlt';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import EventIcon from '@material-ui/icons/Event';
import AssignmentIcon from '@material-ui/icons/Assignment';
import {
    RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer
} from 'recharts';
import jsPDF from 'jspdf'
import * as htmlToImage from 'html-to-image';
import Helper from '../../../common/Helper';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import { AppDataContext } from '../../../common/AppContext'

// ─── Constants ────────────────────────────────────────────────────────────────

const CATEGORY_BORDER_COLORS = [
    '#2563eb', '#16a34a', '#d97706', '#ea580c', '#0891b2', '#7c3aed', '#db2777',
];

const CATEGORY_WEIGHTS = {
    'court presence': 30,
    'mechanics': 20,
    'play calling': 20,
    'communication': 15,
    'fitness': 15,
};

const getCategoryWeight = (name) =>
    CATEGORY_WEIGHTS[(name || '').trim().toLowerCase()] || 0;

const getProspectName = (form) => {
    const fullName = `${form?.firstName || ''} ${form?.lastName || ''}`.trim();
    return (form?.refereeName || fullName || '').trim();
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const getScoreBadgeStyle = (score) => {
    if (score >= 4.0) return { backgroundColor: '#dcfce7', color: '#16a34a' };
    if (score >= 3.0) return { backgroundColor: '#fef9c3', color: '#ca8a04' };
    return { backgroundColor: '#fee2e2', color: '#dc2626' };
};

const getCategoryScoreBadgeStyle = (score) => {
    if (score >= 4.0) return '#16a34a';
    if (score >= 3.0) return '#ca8a04';
    return '#dc2626';
};

const getCategoryScore = (cat, form) => {
    const activeSubs = (cat.subCategories || []).filter((s) => s.subCategoryActive);
    if (activeSubs.length > 0) {
        const ratings = activeSubs.map((s) => form[`sub_${s.id}`] || 0);
        const valid = ratings.filter((r) => r > 0);
        if (valid.length > 0) {
            return valid.reduce((a, b) => a + b, 0) / valid.length;
        }
        // Fallback for old-format evaluations rated at the category level
        return form[`cat_${cat.id}`] || 0;
    }
    return form[`cat_${cat.id}`] || 0;
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const StarRating = ({ value, max = 5 }) => (
    <Box display="inline-flex" alignItems="center">
        {[...Array(max)].map((_, i) => (
            <span key={i} style={{ color: i < value ? '#f59e0b' : '#d1d5db', fontSize: 14 }}>
                ★
            </span>
        ))}
    </Box>
);

const ScoreGauge = ({ score, maxScore = 5 }) => {
    const pct = Math.round((score / maxScore) * 100);
    const r = 56;
    const circ = 2 * Math.PI * r;
    const offset = circ - (pct / 100) * circ;

    return (
        <svg width="150" height="150" viewBox="0 0 150 150">
            <circle cx="75" cy="75" r={r} fill="none" stroke="#e5e7eb" strokeWidth="11" />
            <circle
                cx="75" cy="75" r={r}
                fill="none" stroke="#1d428a" strokeWidth="11"
                strokeDasharray={circ} strokeDashoffset={offset}
                strokeLinecap="round" transform="rotate(-90 75 75)"
            />
            <text x="75" y="68" textAnchor="middle" fontSize="26" fontWeight="bold" fill="#1d428a">
                {pct}%
            </text>
            <text x="75" y="88" textAnchor="middle" fontSize="10" fill="#666">
                Weighted Score
            </text>
            <text x="75" y="102" textAnchor="middle" fontSize="10" fill="#666">
                ({score.toFixed(2)}/{maxScore})
            </text>
        </svg>
    );
};

const CategoryScoreBadge = ({ score }) => {
    const style = getScoreBadgeStyle(score);
    return (
        <Chip
            label={`Calculated Score: ${score.toFixed(2)}/5`}
            size="small"
            style={{
                ...style,
                fontWeight: 600,
                fontSize: '0.7rem',
                height: 22,
            }}
        />
    );
};

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles((theme) => ({
    dialogPaper: {
        maxWidth: 1100,
        width: '100%',
    },
    page: {
        backgroundColor: '#eef2f7',
        padding: theme.spacing(3),
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        position: 'relative',
        '@media print': {
            padding: theme.spacing(2),
            backgroundColor: '#eef2f7 !important',
            '-webkit-print-color-adjust': 'exact',
            colorAdjust: 'exact',
        },
    },
    reportTitle: {
        fontWeight: 700,
        fontSize: '1.35rem',
        color: '#1a1a2e',
        marginBottom: theme.spacing(2),
        '@media print': { fontSize: '1.1rem' },
    },
    reportTitle_wo_bottom_margin: {
        fontWeight: 700,
        fontSize: '1.35rem',
        color: '#1a1a2e',
        '@media print': { fontSize: '1.1rem' },
    },
    topCard: {
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(2),
        borderRadius: 10,
        backgroundColor: '#ffffff',
    },
    infoLabel: {
        fontWeight: 600,
        fontSize: '0.82rem',
        color: '#333',
    },
    infoValue: {
        fontSize: '0.82rem',
        color: '#555',
        fontWeight: 400,
    },
    summaryHeading: {
        fontWeight: 700,
        fontSize: '0.9rem',
        color: '#1a1a2e',
        marginBottom: theme.spacing(0.5),
    },
    summaryCountRow: {
        display: 'flex',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
        marginBottom: theme.spacing(1),
    },
    summaryCountColumn: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: theme.spacing(0.75),
    },
    summaryHeadingRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(0.5),
        marginBottom: theme.spacing(0.5),
    },
    summaryPopover: {
        maxWidth: 720,
        minWidth: 340,
        maxHeight: 360,
        borderRadius: 10,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 8px 24px rgba(12, 30, 65, 0.25)',
    },
    summaryPopoverHeader: {
        flex: '0 0 auto',
        zIndex: 2,
        background: '#1d428a',
        color: '#fff',
        padding: '10px 8px 10px 16px',
        fontWeight: 700,
        fontSize: '0.9rem',
        letterSpacing: '0.02em',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
    },
    summaryPopoverHeaderTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
    },
    summaryPopoverCloseButton: {
        color: '#fff',
        padding: 4,
    },
    summaryPopoverBody: {
        flex: '1 1 auto',
        overflowY: 'auto',
        maxHeight: 300,
        padding: theme.spacing(1.5),
    },
    summaryTable: {
        width: '100%',
        borderCollapse: 'collapse',
        fontSize: '0.72rem',
    },
    summaryTableHead: {
        textAlign: 'left',
        padding: '6px 8px',
        color: '#fff',
        fontWeight: 700,
        backgroundColor: '#1d428a',
        whiteSpace: 'nowrap',
        position: 'sticky',
        top: 0,
    },
    summaryTableCell: {
        padding: '6px 8px',
        color: '#333',
        borderBottom: '1px solid #f1f5f9',
        verticalAlign: 'top',
    },
    summaryTableRowAlt: {
        backgroundColor: '#f8fafc',
    },
    summaryCountChip: {
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 22,
        backgroundColor: '#e8eefc',
        color: '#1d428a',
    },
    summaryScroll: {
        maxHeight: 150,
        overflowY: 'auto',
        paddingRight: 4,
    },
    summaryItem: {
        paddingBottom: theme.spacing(0.75),
        marginBottom: theme.spacing(0.75),
        borderBottom: '1px dashed #e0e0e0',
        '&:last-child': { borderBottom: 'none', marginBottom: 0, paddingBottom: 0 },
    },
    summaryProspectName: {
        fontWeight: 700,
        fontSize: '0.88rem',
        color: '#1d428a',
    },
    summaryDetail: {
        fontSize: '0.82rem',
        color: '#333',
        fontWeight: 600,
        lineHeight: 1.5,
    },
    categoryCard: {
        padding: theme.spacing(2),
        borderRadius: 10,
        backgroundColor: '#ffffff',
        borderLeft: '4px solid #ccc',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
    },
    categoryHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1.5),
        flexWrap: 'wrap',
        gap: theme.spacing(0.5),
    },
    categoryName: {
        fontWeight: 700,
        fontSize: '0.9rem',
        color: '#1a1a2e',
    },
    subRatingRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(0.5),
    },
    subLabel: {
        fontSize: '0.72rem',
        color: '#555',
        marginTop: 2,
        fontWeight: 700
    },
    ratingValue: {
        fontSize: '0.72rem',
        fontWeight: 600,
        color: '#333',
        marginLeft: 4,
    },
    commentSection: {
        marginTop: theme.spacing(1),
        paddingTop: theme.spacing(1),
    },
    commentLabel: {
        fontWeight: 700,
        fontSize: '0.78rem',
        color: '#333',
    },
    commentText: {
        fontSize: '0.76rem',
        color: '#555',
        lineHeight: 1.5,
        marginTop: 2,
        wordBreak: 'break-word',
    },
    footer: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginTop: theme.spacing(2),
        paddingTop: theme.spacing(1),
        borderTop: '1px solid #cfd8e3',
        fontSize: '0.7rem',
        color: '#888',
        '@media print': { fontSize: '0.65rem' },
    },
    toolbarRow: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: theme.spacing(1),
        marginBottom: theme.spacing(1),
        '@media print': { display: 'none' },
    },
    competencyChip: {
        fontSize: '0.65rem',
        height: 20,
        margin: 2,
    },
}));

// ─── Main Component ───────────────────────────────────────────────────────────

const EvaluationReport = ({
    open,
    onClose,
    form,
    evaluationCategories,
    competencies,
    selectedCompetencies,
    tangibles,
    selectedTangibles,
    selectedEvent,
    onSwitchToText,
    showEventDetails = true,
    evaluationsInfo = null
}) => {
    const classes = useStyles();
    const prospectName = getProspectName(form);

    const detailsRef = useRef(null);


    const handleExportPDF = () => {
        // Call child's exportPDF via ref
        detailsRef.current?.exportPDF();
    };

    if (!form) return null;
    // ── Render ───────────────────────────────────────────────────────────────────

    return (
        <Dialog open={open} onClose={onClose} maxWidth={false} classes={{ paper: classes.dialogPaper }} scroll="body" disableBackdropClick>
            <DialogContent style={{ padding: 0 }}>
                {/* Toolbar */}
                <Box className={classes.toolbarRow} style={{ padding: '12px 16px 0', justifyContent: 'space-between' }}>
                    <Box display="flex" alignItems="center" gridGap={8}>
                        {onSwitchToText && (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ListAltIcon />}
                                onClick={onSwitchToText}
                                style={{ textTransform: 'none', fontSize: '0.75rem' }}
                            >
                                View Text Report
                            </Button>
                        )}
                    </Box>
                    <Box display="flex" alignItems="center" gridGap={8}>
                        <Tooltip title="Download PDF">
                            <IconButton size="small" onClick={handleExportPDF}>
                                <PictureAsPdfIcon style={{ color: '#d32f2f' }} />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Close">
                            <IconButton size="small" onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>

                {/* Report body */}
                <Box className={classes.page} >
                    <Typography className={ showEventDetails ? classes.reportTitle : classes.reportTitle_wo_bottom_margin }>
                        Basketball Officiating Evaluation Report
                    </Typography>
                    {!showEventDetails && (
                        <Typography className={classes.infoLabel} style={{ marginBottom: 12 }}>
                            Prospect Name: <span className={classes.infoValue}>{prospectName || 'N/A'}</span>
                        </Typography>
                    )}

                    <EvaluationReportDetails
                        ref={detailsRef}
                        form={form}
                        evaluationCategories={evaluationCategories}
                        competencies={competencies}
                        selectedCompetencies={selectedCompetencies}
                        tangibles={tangibles}
                        selectedTangibles={selectedTangibles}
                        selectedEvent={selectedEvent}
                        showEventDetails={showEventDetails}
                        evaluationsInfo={evaluationsInfo}

                    />
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export const EvaluationReportDetails = React.forwardRef(({
    form,
    evaluationCategories,
    competencies,
    selectedCompetencies,
    tangibles,
    selectedTangibles,
    selectedEvent,
    showEventDetails,
    evaluationsInfo = null,
    compact = false

}, ref) => {
    // Component logic here
    const classes = useStyles();
    const { addSnackMessages } = useContext(AppDataContext)
    const reportRef = useRef(null);
    const prospectName = getProspectName(form);
    const [summaryAnchorEl, setSummaryAnchorEl] = useState(null);
    const [summaryPopoverType, setSummaryPopoverType] = useState(null); // 'events' | 'evaluations'
    const isSummaryOpen = Boolean(summaryAnchorEl);
    const allEvaluations = (evaluationsInfo?.prospects || [])
        .flatMap((p) => (p.evaluations || []).map((ev) => ({ ...ev, prospectName: p.name })))
        // Always show the latest review date first, regardless of the order the
        // evaluations were provided in by the calling screen.
        .sort((a, b) => {
            const da = a.evaluationDate ? new Date(a.evaluationDate).getTime() : 0;
            const db = b.evaluationDate ? new Date(b.evaluationDate).getTime() : 0;
            return db - da;
        });
    // Distinct list of events across all selected evaluations. Multiple
    // evaluations for the same prospect (or different prospects) in the same
    // event are collapsed into a single entry keyed by the event's identifying
    // fields (name, dates, game type, and location).
    const distinctEvents = (() => {
        const map = new Map();
        allEvaluations.forEach((ev) => {
            const key = [
                ev.eventName || '',
                ev.eventFrom || '',
                ev.eventTo || '',
                ev.gameTypeName || '',
                ev.cityState || '',
                ev.countryName || '',
            ].join('|').toLowerCase();
            if (!map.has(key)) {
                map.set(key, {
                    eventName: ev.eventName,
                    eventFrom: ev.eventFrom,
                    eventTo: ev.eventTo,
                    gameTypeName: ev.gameTypeName,
                    cityState: ev.cityState,
                    countryName: ev.countryName,
                });
            }
        });
        return Array.from(map.values());
    })();
    const activeCategories = (evaluationCategories || []).filter((c) => c.categoryActive);

    // Build enriched category list with predefined weights
    const categoryData = activeCategories.map((cat, idx) => {
        const score = getCategoryScore(cat, form);
        const weight = getCategoryWeight(cat.categoryName);
        const weightedContribution = score * (weight / 100);
        return {
            ...cat,
            score,
            weight,
            weightedContribution,
            borderColor: CATEGORY_BORDER_COLORS[idx % CATEGORY_BORDER_COLORS.length],
        };
    });

    // Weighted overall score: sum(category_avg × weight%)
    const totalWeight = categoryData.reduce((sum, c) => sum + c.weight, 0);
    const overallScore =
        totalWeight > 0
            ? categoryData.reduce((sum, c) => sum + c.weightedContribution, 0)
            : 0;

    // Radar chart data
    const radarData = categoryData.map((c) => ({
        category: `${c.categoryName} (${c.weight}%)`,
        score: (c.score / 5) * 100,
        fullMark: 100,
    }));

    // Gather competency names for a given key
    const getCompetencyNames = (key) =>
        (selectedCompetencies[key] || [])
            .map((cId) => (competencies || []).find((c) => c.id === cId))
            .filter(Boolean)
            .map((c) => c.competencyName);

    // Selected tangible names
    const selectedTangibleNames = (tangibles || [])
        .filter((t) => (selectedTangibles || []).includes(t.id))
        .map((t) => t.name);

    // Event display helpers
    const eventName = selectedEvent?.eventName || '';
    const locationParts = [
        selectedEvent?.stateName || selectedEvent?.stateOther || '',
        selectedEvent?.city || '',
    ].filter(Boolean);
    const locationStr = useMemo(() => {
        let address = []
        if (locationParts.length > 0) {

            if (selectedEvent?.city) {
                address.push(`${selectedEvent.city}`)
            }
            if (selectedEvent.stateName || selectedEvent.stateOther) {
                address.push(`${selectedEvent.stateName || selectedEvent.stateOther || ''}`)
            }
            if (selectedEvent?.countryName) {
                address.push(`${selectedEvent.countryName}`)
            }

            return address.join(', ');
        }
        return '';
    }, [locationParts, selectedEvent]);
    const gameLabel = selectedEvent?.gameTypeName || '';
    const scoutingCategoryLabel = selectedEvent?.scoutingCategoryName || '';
    const scoutingSubCategoryLabel = selectedEvent?.scoutingSubCategoryName || '';

    useImperativeHandle(ref, () => ({

        exportPDF: exportPDF,
        getReportElement: () => {
            return reportRef.current;
        },
    }));

    const exportPDF = () => {
        // Use html-to-image to render the report as an image, then add to PDF
        if (!reportRef.current) return;
        const node = reportRef.current;
        addSnackMessages([{
            open: true,
            message: "Downloading report.",
            severity: "success",
            hideDuration: 8000
        }]);

        // Temporarily expand any scrollable containers so nothing is clipped in the export.
        const scrollEls = node.querySelectorAll('[data-pdf-expand]');
        const savedStyles = [];
        scrollEls.forEach((el) => {
            savedStyles.push({ el, maxHeight: el.style.maxHeight, overflowY: el.style.overflowY });
            el.style.maxHeight = 'none';
            el.style.overflowY = 'visible';
        });

        // Temporarily hide any elements that should be omitted from the PDF export.
        const hideEls = node.querySelectorAll('[data-pdf-hide]');
        const savedHidden = [];
        hideEls.forEach((el) => {
            savedHidden.push({ el, display: el.style.display });
            el.style.display = 'none';
        });

        const restoreScrollStyles = () => {
            savedStyles.forEach(({ el, maxHeight, overflowY }) => {
                el.style.maxHeight = maxHeight;
                el.style.overflowY = overflowY;
            });
            savedHidden.forEach(({ el, display }) => {
                el.style.display = display;
            });
        };

        htmlToImage.toPng(node, { backgroundColor: '#fff', pixelRatio: 2 }).then((dataUrl) => {
            restoreScrollStyles();
            // Set PDF margin (e.g., 24pt = 8mm)
            const margin = 24;
            const pdf = new jsPDF('p', 'pt', 'a4');
            const imgProps = pdf.getImageProperties(dataUrl);
            const pdfWidth = pdf.internal.pageSize.getWidth() - margin * 2;
            const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(14);
            pdf.text('Basketball Officiating Evaluation Report', margin, margin + 10);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(11);
            pdf.text(`Prospect Name: ${prospectName || 'N/A'}`, margin, margin + 26);
            pdf.addImage(dataUrl, 'PNG', margin, margin + 36, pdfWidth, pdfHeight);
            pdf.save('evaluation.pdf');
        }).catch(() => {
            restoreScrollStyles();
        });
        // pdf.html(reportRef.current, {
        //     callback: async (pdf) => {
        //         awaitpdf.save('evaluation.pdf');
        //     },
        // });
    };

    return (
        <div ref={reportRef}>
            {/* ── Top Section: Info + Gauge + Radar ─────────────────────────── */}
            <Paper className={classes.topCard} elevation={0} variant="outlined">
                <Grid container spacing={2} direction={compact ? 'column' : 'row'}>
                    {/* Left – Info */}
                    {showEventDetails && <Grid item xs={12} sm={compact ? 12 : 4} style={{ display: 'flex' }}>
                        <Box component="div" justifyContent="space-around" flexDirection="column" display="flex" style={compact ? { gap: 6 } : {}}>
                            <Typography className={classes.infoLabel}>
                                Prospect Name:{' '}
                                <span className={classes.infoValue}>{prospectName || 'N/A'}</span>
                            </Typography>
                            {eventName && (
                                <Typography className={classes.infoLabel}>
                                    Event: <span className={classes.infoValue}>{eventName}</span>
                                </Typography>
                            )}
                            {form.evaluationDate && <Typography className={classes.infoLabel}>
                                Review Date:{' '}
                                <span className={classes.infoValue}>{form.evaluationDate || 'N/A'}</span>
                            </Typography>}
                            {selectedEvent?.eventFrom && (
                                <Typography className={classes.infoLabel}>
                                    Event Start:{' '}
                                    <span className={classes.infoValue}>{Helper.FormatDate(selectedEvent.eventFrom)}</span>
                                </Typography>
                            )}
                            {selectedEvent?.eventTo && (
                                <Typography className={classes.infoLabel}>
                                    Event End:{' '}
                                    <span className={classes.infoValue}>{Helper.FormatDate(selectedEvent.eventTo)}</span>
                                </Typography>
                            )}
                            {locationStr && (
                                <Typography className={classes.infoLabel}>
                                    Location: <span className={classes.infoValue}>{locationStr}</span>
                                </Typography>
                            )}
                            {gameLabel && scoutingCategoryLabel=='' && scoutingSubCategoryLabel=='' ? (
                                <Typography className={classes.infoLabel}>
                                    Game Type: <span className={classes.infoValue}>{gameLabel}</span>
                                </Typography>
                            ) :  (
                                <>
                                    <Typography className={classes.infoLabel}>
                                        Category Name: <span className={classes.infoValue}>{scoutingCategoryLabel}</span>
                                    </Typography>
                                    <Typography className={classes.infoLabel}>
                                        Sub Category: <span className={classes.infoValue}>{scoutingSubCategoryLabel}</span>
                                    </Typography>
                                </>
                            ) }
                            
                        </Box>
                    </Grid>}

                    {/* Center – Gauge */}
                    <Grid item xs={12} sm={compact ? 12 : 4} style={{ textAlign: 'center', alignItems: 'center', display: 'flex', justifyContent: 'center' }}>
                        <ScoreGauge score={overallScore} />
                    </Grid>

                    {/* Right – Radar Chart */}
                    <Grid item xs={12} sm={compact ? 12 : 4} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        {radarData.length > 0 && (
                            <ResponsiveContainer width="100%" height={180}>
                                <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
                                    <PolarGrid stroke="#d1d5db" />
                                    <PolarAngleAxis
                                        dataKey="category"
                                        tick={{ fontSize: 9, fill: '#555' }}
                                    />
                                    <PolarRadiusAxis
                                        angle={90}
                                        domain={[0, 100]}
                                        tick={{ fontSize: 8 }}
                                        tickCount={6}
                                        tickFormatter={(v) => `${v}%`}
                                    />
                                    <Radar
                                        dataKey="score"
                                        stroke="#1d428a"
                                        fill="#1d428a"
                                        fillOpacity={0.25}
                                        strokeWidth={2}
                                    />
                                </RadarChart>
                            </ResponsiveContainer>
                        )}
                    </Grid>

                    {/* Right – Selected prospects & evaluations summary (consolidated report) */}
                    {!showEventDetails && evaluationsInfo && (
                        <Grid item xs={12} sm={compact ? 12 : 4} style={{ display: 'flex' }}>
                            <Box component="div" flexDirection="column" display="flex" width="100%">
                                <Box className={classes.summaryCountColumn}>
                                    <Box display="flex" alignItems="center" gridGap={4}>
                                        <Typography style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a2e' }}>
                                            Events: {distinctEvents.length}
                                        </Typography>
                                        <Tooltip title="View events">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => { setSummaryPopoverType('events'); setSummaryAnchorEl(e.currentTarget); }}
                                                data-pdf-hide
                                            >
                                                <ErrorOutlineIcon fontSize="small" style={{ color: '#1d428a' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                    <Box display="flex" alignItems="center" gridGap={4}>
                                        <Typography style={{ fontWeight: 700, fontSize: '0.85rem', color: '#1a1a2e' }}>
                                            Evaluations: {evaluationsInfo.evaluationCount}
                                        </Typography>
                                        <Tooltip title="View evaluations">
                                            <IconButton
                                                size="small"
                                                onClick={(e) => { setSummaryPopoverType('evaluations'); setSummaryAnchorEl(e.currentTarget); }}
                                                data-pdf-hide
                                            >
                                                <ErrorOutlineIcon fontSize="small" style={{ color: '#1d428a' }} />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            </Box>

                            <Popover
                                open={isSummaryOpen}
                                anchorEl={summaryAnchorEl}
                                onClose={() => setSummaryAnchorEl(null)}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                                PaperProps={{ className: classes.summaryPopover }}
                            >
                                {summaryPopoverType === 'events' ? (
                                    // List the distinct events
                                    <>
                                        <Box className={classes.summaryPopoverHeader}>
                                            <span className={classes.summaryPopoverHeaderTitle}>
                                                <EventIcon fontSize="small" />
                                                Events ({distinctEvents.length})
                                            </span>
                                            <IconButton
                                                size="small"
                                                className={classes.summaryPopoverCloseButton}
                                                onClick={() => setSummaryAnchorEl(null)}
                                                aria-label="Close"
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        <Box className={classes.summaryPopoverBody}>
                                        <table className={classes.summaryTable}>
                                            <thead>
                                                <tr>
                                                    <th className={classes.summaryTableHead}>Event</th>
                                                    <th className={classes.summaryTableHead}>Event Dates</th>
                                                    <th className={classes.summaryTableHead}>Game Type</th>
                                                    <th className={classes.summaryTableHead}>Location</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {distinctEvents.map((ev, idx) => {
                                                    const location = [ev.cityState, ev.countryName]
                                                        .filter(Boolean)
                                                        .join(', ');
                                                    return (
                                                        <tr key={idx} className={idx % 2 === 1 ? classes.summaryTableRowAlt : undefined}>
                                                            <td className={classes.summaryTableCell} style={{ fontWeight: 700, color: '#1d428a' }}>
                                                                {ev.eventName || 'N/A'}
                                                            </td>
                                                            <td className={classes.summaryTableCell} style={{ whiteSpace: 'nowrap' }}>
                                                                {ev.eventFrom || ev.eventTo ? (
                                                                    <>
                                                                        {ev.eventFrom && <div>{ev.eventFrom}</div>}
                                                                        {ev.eventTo && <div>{ev.eventTo}</div>}
                                                                    </>
                                                                ) : '-'}
                                                            </td>
                                                            <td className={classes.summaryTableCell}>
                                                                {ev.gameTypeName || '-'}
                                                            </td>
                                                            <td className={classes.summaryTableCell}>
                                                                {location || '-'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        </Box>
                                    </>
                                ) : (
                                    // List evaluations in a compact table
                                    <>
                                        <Box className={classes.summaryPopoverHeader}>
                                            <span className={classes.summaryPopoverHeaderTitle}>
                                                <AssignmentIcon fontSize="small" />
                                                Evaluations ({allEvaluations.length})
                                            </span>
                                            <IconButton
                                                size="small"
                                                className={classes.summaryPopoverCloseButton}
                                                onClick={() => setSummaryAnchorEl(null)}
                                                aria-label="Close"
                                            >
                                                <CloseIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                        <Box className={classes.summaryPopoverBody}>
                                        <table className={classes.summaryTable}>
                                            <thead>
                                                <tr>
                                                    <th className={classes.summaryTableHead}>Event</th>
                                                    {evaluationsInfo.prospectCount > 1 && (
                                                        <th className={classes.summaryTableHead}>Prospect</th>
                                                    )}
                                                    <th className={classes.summaryTableHead}>Review Date</th>
                                                    <th className={classes.summaryTableHead}>Event Dates</th>
                                                    <th className={classes.summaryTableHead}>Game Type</th>
                                                    <th className={classes.summaryTableHead}>Location</th>
                                                    <th className={classes.summaryTableHead}>Score</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {allEvaluations.map((ev, idx) => {
                                                    const location = [ev.cityState, ev.countryName]
                                                        .filter(Boolean)
                                                        .join(', ');
                                                    return (
                                                        <tr key={idx} className={idx % 2 === 1 ? classes.summaryTableRowAlt : undefined}>
                                                            <td className={classes.summaryTableCell} style={{ fontWeight: 700, color: '#1d428a' }}>
                                                                {ev.eventName || 'N/A'}
                                                            </td>
                                                            {evaluationsInfo.prospectCount > 1 && (
                                                                <td className={classes.summaryTableCell}>
                                                                    {ev.prospectName || '-'}
                                                                </td>
                                                            )}
                                                            <td className={classes.summaryTableCell} style={{ whiteSpace: 'nowrap' }}>
                                                                {ev.evaluationDate || '-'}
                                                            </td>
                                                            <td className={classes.summaryTableCell} style={{ whiteSpace: 'nowrap' }}>
                                                                {ev.eventFrom || ev.eventTo ? (
                                                                    <>
                                                                        {ev.eventFrom && <div>{ev.eventFrom}</div>}
                                                                        {ev.eventTo && <div>{ev.eventTo}</div>}
                                                                    </>
                                                                ) : '-'}
                                                            </td>
                                                            <td className={classes.summaryTableCell}>
                                                                {ev.gameTypeName || '-'}
                                                            </td>
                                                            <td className={classes.summaryTableCell}>
                                                                {location || '-'}
                                                            </td>
                                                            <td className={classes.summaryTableCell} style={{ whiteSpace: 'nowrap', fontWeight: 700 }}>
                                                                {ev.score && ev.score !== '—' ? `${ev.score}/5` : '-'}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                        </Box>
                                    </>
                                )}
                            </Popover>
                        </Grid>
                    )}
                </Grid>
            </Paper>

            {/* ── Category Cards ────────────────────────────────────────────── */}
            <Grid container spacing={2}>
                {categoryData.map((cat) => {
                    const activeSubs = (cat.subCategories || []).filter((s) => s.subCategoryActive);
                    const comment = form[`category_${cat.id}`] || '';

                    return (
                        <Grid item xs={12} sm={compact ? 12 : 6} md={compact ? 12 : 4} key={cat.id} style={{ display: 'flex' }}>
                            <Paper
                                className={classes.categoryCard}
                                elevation={0}
                                variant="outlined"
                                style={{ borderLeftColor: getCategoryScoreBadgeStyle(cat.score) }}
                            >
                                {/* Header */}
                                <Box className={classes.categoryHeader}>
                                    <Typography className={classes.categoryName}>
                                        {cat.categoryName} ({cat.weight}%)
                                    </Typography>
                                    <CategoryScoreBadge score={cat.score} />
                                </Box>

                                {/* Weighted contribution */}
                                <Box style={{ marginBottom: 8, padding: '4px 8px', backgroundColor: '#f8fafc', borderRadius: 6, fontSize: '0.72rem', color: '#475569' }}>
                                    {cat.score.toFixed(2)} × {cat.weight}% = <strong>{cat.weightedContribution.toFixed(2)}</strong>
                                </Box>

                                {/* Subcategory ratings */}
                                {activeSubs.length > 0 ? (
                                    <Box>
                                        {activeSubs.map((sub) => {
                                            const rating = form[`sub_${sub.id}`] || 0;
                                            const compNames = getCompetencyNames(`sub_comp_${sub.id}`);
                                            return (
                                                <>
                                                    <Box key={sub.id} mb={1}>
                                                        <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 4 }}>
                                                            <Typography className={classes.subLabel}>
                                                                {sub.subCategoryName}
                                                            </Typography>
                                                            <StarRating value={rating} />
                                                            <span className={classes.ratingValue}>{rating}/5</span>
                                                        </Box>
                                                        {compNames.length > 0 && (
                                                            <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                                                                {compNames.map((name) => (
                                                                    <Chip
                                                                        key={name}
                                                                        label={name}
                                                                        size="small"
                                                                        className={classes.competencyChip}
                                                                        style={{ backgroundColor: '#e0f2fe', color: '#0369a1' }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        )}
                                                    </Box>
                                                    <Divider style={{ margin: '8px 0' }} />
                                                </>
                                            );
                                        })}
                                    </Box>
                                ) : (
                                    <Box>
                                        <Box mb={1}>
                                            <Box display="flex" alignItems="center" style={{ gap: 8, marginBottom: 4 }}>
                                                <Typography className={classes.subLabel}>
                                                    {cat.categoryName}
                                                </Typography>
                                                <StarRating value={form[`cat_${cat.id}`] || 0} />
                                                <span className={classes.ratingValue}>
                                                    {form[`cat_${cat.id}`] || 0}/5
                                                </span>
                                            </Box>

                                            {/* Category-level competency chips */}
                                            {(() => {
                                                const names = getCompetencyNames(`cat_comp_${cat.id}`);
                                                return names.length > 0 ? (
                                                    <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
                                                        {names.map((name) => (
                                                            <Chip
                                                                key={name}
                                                                label={name}
                                                                size="small"
                                                                className={classes.competencyChip}
                                                                style={{ backgroundColor: '#e0f2fe', color: '#0369a1' }}
                                                            />
                                                        ))}
                                                    </Box>
                                                ) : null;
                                            })()}
                                        </Box>
                                    </Box>
                                )}

                                {/* Comment */}

                                <Box className={classes.commentSection} style={{ marginTop: 'auto' }}>
                                    <Typography className={classes.commentLabel}>Comment:</Typography>
                                    <Typography className={classes.commentText}>
                                        {comment !== "" ? comment : "No comment"}
                                    </Typography>
                                </Box>

                            </Paper>
                        </Grid>
                    );
                })}
            </Grid>

            {/* ── Score Breakdown ────────────────────────────────────────── */}
            {categoryData.length > 0 && (
                <Paper
                    elevation={0}
                    variant="outlined"
                    style={{ padding: 16, marginTop: 8, borderRadius: 10, backgroundColor: '#fff' }}
                >
                    <Typography style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 8 }}>
                        Score Breakdown
                    </Typography>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.78rem' }}>
                        <thead>
                            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                                <th style={{ textAlign: 'left', padding: '4px 8px', color: '#475569' }}>Category</th>
                                <th style={{ textAlign: 'center', padding: '4px 8px', color: '#475569' }}>Avg Score</th>
                                <th style={{ textAlign: 'center', padding: '4px 8px', color: '#475569' }}>Weight</th>
                                <th style={{ textAlign: 'center', padding: '4px 8px', color: '#475569' }}>Contribution</th>
                            </tr>
                        </thead>
                        <tbody>
                            {categoryData.map((cat) => (
                                <tr key={cat.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '6px 8px', fontWeight: 500 }}>{cat.categoryName}</td>
                                    <td style={{ textAlign: 'center', padding: '6px 8px' }}>{cat.score.toFixed(2)}</td>
                                    <td style={{ textAlign: 'center', padding: '6px 8px' }}>{cat.weight}%</td>
                                    <td style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 600 }}>{cat.weightedContribution.toFixed(2)}</td>
                                </tr>
                            ))}
                            <tr style={{ borderTop: '2px solid #1d428a' }}>
                                <td colSpan={3} style={{ padding: '6px 8px', fontWeight: 700, color: '#1d428a' }}>Overall Weighted Score</td>
                                <td style={{ textAlign: 'center', padding: '6px 8px', fontWeight: 700, color: '#1d428a', fontSize: '0.9rem' }}>{overallScore.toFixed(2)}</td>
                            </tr>
                        </tbody>
                    </table>
                </Paper>
            )}

            {/* ── Evaluation Summary ────────────────────────────────────────── */}
            <Paper
                elevation={0}
                variant="outlined"
                style={{ padding: 16, marginTop: 8, borderRadius: 10, backgroundColor: '#fff' }}
            >
                <Typography style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 6 }}>
                    Evaluation Summary
                </Typography>
                <Typography style={{ fontSize: '0.78rem', color: '#555', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                    {form.evaluationSummary && form.evaluationSummary.trim()
                        ? form.evaluationSummary
                        : 'No Comments'}
                </Typography>
            </Paper>

            {/* ── InTangibles ─────────────────────────────────────────────────── */}
            {selectedTangibleNames.length > 0 && (
                <Paper
                    elevation={0}
                    variant="outlined"
                    style={{ padding: 16, marginTop: 8, borderRadius: 10, backgroundColor: '#fff' }}
                >
                    <Typography style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 6 }}>
                        Intangibles
                    </Typography>
                    <Box display="flex" flexWrap="wrap" style={{ gap: 6 }}>
                        {selectedTangibleNames.map((name) => (
                            <Chip
                                key={name}
                                label={name}
                                size="small"
                                style={{
                                    backgroundColor: '#f0fdf4',
                                    color: '#15803d',
                                    fontWeight: 500,
                                    fontSize: '0.7rem',
                                    height: 22,
                                }}
                            />
                        ))}
                    </Box>
                </Paper>
            )}
        </div>
    )
});


export default EvaluationReport;
